import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Button, Input, Textarea } from '../../components/UI';
import { territoriesAPI, usersAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { HiOutlineLocationMarker, HiOutlineTrash, HiOutlinePlus } from 'react-icons/hi';

// Extract complete 6-digit Indian pincodes from free text (comma separated list)
const extractPincodes = (text) => (String(text).match(/\b[1-9][0-9]{5}\b/g) || []);

const EMPTY_FORM = {
  name: '',
  description: '',
  color: '#10B981',
  states: '',
  cities: '',
  pincodes: '',
  assignedUserIds: [],
};

const COLOR_OPTIONS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#6B7280'];

const TerritoryModal = ({ isOpen, onClose, territory }) => {
  const queryClient = useQueryClient();
  const isEdit = Boolean(territory?._id);

  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [managerId, setManagerId] = useState('');

  // Pincode auto-lookup: the LAST pincode in the field fills the Cities/States fields
  const [pincodeHint, setPincodeHint] = useState(null); // { type: 'loading'|'success'|'error', text }
  const latestPincodes = useRef('');
  const lastResolved = useRef(null);
  const lookupInFlight = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    latestPincodes.current = territory?.pincodes?.join(', ') || '';
    lookupInFlight.current = false;
    lastResolved.current = extractPincodes(latestPincodes.current).slice(-1)[0] || null;
    setPincodeHint(null);
    if (territory) {
      setForm({
        name: territory.name || '',
        description: territory.description || '',
        color: territory.color || '#10B981',
        states: (territory.states || []).join(', '),
        cities: (territory.city_ids || []).join(', '),
        pincodes: (territory.pincodes || []).join(', '),
        assignedUserIds: (territory.assigned_user_ids || []).map((u) => u._id || u),
      });
      setManagerId(territory.manager_id?._id || territory.manager_id || '');
    } else {
      setForm({ ...EMPTY_FORM });
      setManagerId('');
    }
  }, [isOpen, territory]);

  // Telecallers + managers for the agent pool
  const { data: users } = useQuery({
    queryKey: ['territoryAssignableUsers'],
    queryFn: async () => {
      const response = await usersAPI.getAll({ role: 'telecaller', limit: 200 });
      return response.data?.data || [];
    },
    enabled: isOpen,
  });

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // Resolve the LAST pincode in the field and OVERWRITE Cities/States with its
  // city + state — earlier pincodes in the list don't affect these fields.
  const resolveLastPincode = () => {
    if (lookupInFlight.current) return;
    const list = extractPincodes(latestPincodes.current);
    const last = list[list.length - 1];
    if (!last || last === lastResolved.current) return;

    lastResolved.current = last;
    lookupInFlight.current = true;
    setPincodeHint({ type: 'loading', text: `Looking up pincode ${last}…` });
    territoriesAPI.pincodeLookup(last)
      .then((res) => {
        const d = res.data?.data || {};
        if (d.city || d.state) {
          setForm((prev) => ({
            ...prev,
            cities: d.city || prev.cities,
            states: d.state || prev.states,
          }));
          const place = [d.city, d.state].filter(Boolean).join(', ');
          setPincodeHint({ type: 'success', text: `${last} → ${place}` });
        } else {
          setPincodeHint({ type: 'error', text: `Pincode ${last} not found — enter the city and state manually` });
        }
      })
      .catch((err) => {
        setPincodeHint({
          type: 'error',
          text: err.response?.data?.message || `Pincode lookup failed for ${last} — enter the city and state manually`,
        });
      })
      .finally(() => {
        lookupInFlight.current = false;
        // Re-check in case the field changed while this lookup was in flight
        resolveLastPincode();
      });
  };

  const handlePincodesChange = (e) => {
    const value = e.target.value;
    latestPincodes.current = value;
    setField('pincodes', value);
    resolveLastPincode();
  };

  const toggleAgent = (id) => {
    setForm((prev) => ({
      ...prev,
      assignedUserIds: prev.assignedUserIds.includes(id)
        ? prev.assignedUserIds.filter((x) => x !== id)
        : [...prev.assignedUserIds, id],
    }));
  };

  const saveMutation = useMutation({
    mutationFn: (data) =>
      isEdit ? territoriesAPI.update(territory._id, data) : territoriesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['territories']);
      toast.success(isEdit ? 'Territory updated successfully' : 'Territory created successfully');
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to save territory');
    },
  });

  const handleSave = () => {
    if (!form.name.trim()) {
      toast.error('Territory name is required');
      return;
    }
    const splitList = (s) => s.split(',').map((v) => v.trim()).filter(Boolean);
    if (splitList(form.states).length === 0 && splitList(form.cities).length === 0 && splitList(form.pincodes).length === 0) {
      toast.error('Define the territory by at least one state, city or pincode');
      return;
    }
    saveMutation.mutate({
      name: form.name.trim(),
      description: form.description,
      color: form.color,
      states: splitList(form.states),
      cities: splitList(form.cities),
      pincodes: splitList(form.pincodes),
      assignedUserIds: form.assignedUserIds,
      managerId: managerId || null,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit Territory' : 'New Territory'} size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Territory name"
            required
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            placeholder="e.g. North Dubai"
          />
          <div className="space-y-1.5">
            <label className="block text-sm font-semibold text-secondary-700">Color</label>
            <div className="flex flex-wrap items-center gap-2 py-1.5">
              {COLOR_OPTIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setField('color', c)}
                  className={`w-7 h-7 rounded-full border-2 transition-all ${form.color === c ? 'border-secondary-900 scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
        </div>

        <Textarea
          label="Description"
          rows={2}
          value={form.description}
          onChange={(e) => setField('description', e.target.value)}
          placeholder="What region does this territory cover?"
        />

        {/* Geography */}
        <div className="border border-secondary-200 rounded-xl p-4 space-y-4">
          <div className="flex items-center gap-2">
            <HiOutlineLocationMarker className="w-5 h-5 text-primary-600" />
            <h3 className="text-sm font-bold text-secondary-900">Geography</h3>
            <span className="text-xs text-secondary-400">(comma separated — leads match by any of these)</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input
              label="States"
              value={form.states}
              onChange={(e) => setField('states', e.target.value)}
              placeholder="e.g. Dubai, Abu Dhabi"
            />
            <Input
              label="Cities"
              value={form.cities}
              onChange={(e) => setField('cities', e.target.value)}
              placeholder="e.g. Deira, Jumeirah"
            />
            <Input
              label="Pincodes"
              value={form.pincodes}
              onChange={handlePincodesChange}
              placeholder="e.g. 110001, 400001"
            />
          </div>
          {pincodeHint && (
            <p
              className={`text-xs mt-0.5 flex items-center gap-1 ${
                pincodeHint.type === 'error'
                  ? 'text-red-600'
                  : pincodeHint.type === 'success'
                    ? 'text-green-600'
                    : 'text-secondary-400'
              }`}
            >
              {pincodeHint.text}
            </p>
          )}
        </div>

        {/* Agents */}
        <div className="border border-secondary-200 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-bold text-secondary-900">
            Territory agents <span className="text-xs font-normal text-secondary-400">(new leads in this region are auto-assigned to them, round-robin)</span>
          </h3>
          {(users || []).length === 0 ? (
            <p className="text-sm text-warning-700 bg-warning-50 border border-warning-200 rounded-lg px-3 py-2">
              No telecallers found in your organization. Add users with the{' '}
              <strong>Telecaller</strong> role in CRM → Users first, then come back to assign them here.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {users.map((u) => (
                <label
                  key={u._id}
                  className={`flex items-center gap-2 p-2.5 rounded-lg border text-sm cursor-pointer transition-colors ${
                    form.assignedUserIds.includes(u._id)
                      ? 'border-primary-300 bg-primary-50 text-primary-700'
                      : 'border-secondary-200 bg-white text-secondary-700 hover:border-primary-200'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={form.assignedUserIds.includes(u._id)}
                    onChange={() => toggleAgent(u._id)}
                    className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="font-medium">{u.firstName} {u.lastName}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 sm:gap-3 justify-end pt-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} loading={saveMutation.isPending}>
            {isEdit ? 'Update Territory' : 'Create Territory'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TerritoryModal;